/**
 * Share ids. Base58 (no 0/O/I/l) because these get read aloud and retyped.
 *
 * 9 characters ≈ 52 bits. Ids are unguessable-by-design rather than secret —
 * anyone with the link can view, which is the intended behaviour for a private
 * group. The edit token is the one that actually has to resist guessing.
 */

const ALPHABET = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';

function randomBytes(n: number): Uint8Array {
  const bytes = new Uint8Array(n);
  const c = globalThis.crypto;
  if (!c?.getRandomValues) {
    throw new Error('crypto.getRandomValues unavailable; cannot mint ids safely');
  }
  c.getRandomValues(bytes);
  return bytes;
}

function base58(length: number): string {
  // Rejection sampling: 256 % 58 != 0, so naive modulo would bias the first
  // 24 symbols. Cheap to do properly.
  const out: string[] = [];
  const limit = 256 - (256 % ALPHABET.length);
  while (out.length < length) {
    for (const byte of randomBytes(length * 2)) {
      if (byte >= limit) continue;
      out.push(ALPHABET[byte % ALPHABET.length]!);
      if (out.length === length) break;
    }
  }
  return out.join('');
}

/** Public, appears in share links. */
export function newRouteId(): string {
  return base58(9);
}

/** Secret, held only by whoever created the route. ~128 bits. */
export function newEditToken(): string {
  return base58(22);
}

/** Local-only ids for waypoints; never persisted anywhere that needs secrecy. */
export function newWaypointId(): string {
  return base58(8);
}

export function isValidRouteId(value: string): boolean {
  return /^[1-9A-HJ-NP-Za-km-z]{9}$/.test(value);
}
